import { FIT_ZOOM_CEILING, MAX_ZOOM, MIN_ZOOM } from './skillGraphLayout';

/**
 * Арифметика панорамы и масштаба (ТЗ 0070).
 *
 * Вынесена из хука намеренно: в jsdom нет ни `PointerEvent`, ни
 * `getBoundingClientRect` с настоящими размерами, а Playwright в проекте не
 * настроен вовсе — значит взаимодействие пальцем здесь непроверяемо. Проверяем
 * то, что можно: арифметику. И говорим об этом вслух, а не делаем вид, что
 * жест покрыт тестом.
 */

export interface IViewport {
  zoom: number;
  x: number;
  y: number;
}

export interface ISize {
  width: number;
  height: number;
}

export const clampZoom = (zoom: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/**
 * Вписать сцену в окно — ПО ШИРИНЕ.
 *
 * 🔴 Высоты в формуле масштаба нет намеренно. Карта разложена лентой: колонки
 * идут слева направо, а навыки всех групп — одним столбцом вниз, поэтому сцена
 * по построению в два-три раза выше, чем шире. Прежняя формула брала худшую
 * ось, то есть высоту: при сцене 888×2048 в окне 1046×480 она давала 0.234,
 * `clampZoom` вытягивал это до нижней границы — и «вписать» НИЧЕГО не
 * вписывало, а просто прыгало в минимальный масштаб, оставляя пустые поля по
 * бокам. Поместить ленту в экран целиком — не цель, а способ сделать подписи
 * нечитаемыми; вертикаль проходится панорамой.
 *
 * 🔴 `FIT_ZOOM_CEILING` = 1: вписывание ТОЛЬКО УМЕНЬШАЕТ. Сцена, которая уже
 * помещается, остаётся в своём масштабе и центрируется (`Math.max(0, …)`
 * ниже) — раздувать её значит показывать три карточки во весь монитор.
 *
 * 🔴 `Math.max(0, …)`: «меньше окна — по центру, больше — прижата к левому
 * верхнему углу». Раньше при высокой сцене `y` выходил отрицательным, и
 * «вписать» показывало СЕРЕДИНУ ленты — ни карточки профессии сверху, ни
 * первой группы. Теперь fit всегда приводит к началу чтения, и `clampPan` это
 * состояние уже считает допустимым.
 */
export const fitToScreen = (scene: ISize, view: ISize): IViewport => {
  if (!scene.width || !scene.height || !view.width || !view.height) {
    return { zoom: 1, x: 0, y: 0 };
  }
  const zoom = clampZoom(Math.min(FIT_ZOOM_CEILING, view.width / scene.width));
  return {
    zoom,
    x: Math.max(0, (view.width - scene.width * zoom) / 2),
    y: Math.max(0, (view.height - scene.height * zoom) / 2),
  };
};

/**
 * Не дать утащить сцену за край.
 *
 * Сцена меньше окна центрируется, большая — держится так, чтобы её край не
 * заезжал внутрь: пустой холст без единой коробки читается как «карта
 * сломалась», а не как «вы прокрутили слишком далеко».
 */
export const clampPan = (viewport: IViewport, scene: ISize, view: ISize): IViewport => {
  const width = scene.width * viewport.zoom;
  const height = scene.height * viewport.zoom;
  const axis = (value: number, size: number, box: number) => {
    if (size <= box) return (box - size) / 2;
    return Math.min(0, Math.max(box - size, value));
  };
  return {
    zoom: viewport.zoom,
    x: axis(viewport.x, width, view.width),
    y: axis(viewport.y, height, view.height),
  };
};

/** Масштаб вокруг точки: под курсором остаётся то же место сцены. */
export const zoomAt = (
  viewport: IViewport,
  factor: number,
  point: { x: number; y: number },
): IViewport => {
  const zoom = clampZoom(viewport.zoom * factor);
  const ratio = zoom / viewport.zoom;
  return {
    zoom,
    x: point.x - (point.x - viewport.x) * ratio,
    y: point.y - (point.y - viewport.y) * ratio,
  };
};

export interface IBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Подвести в поле зрения несколько карточек сразу (ТЗ 0071, п. 6.1).
 *
 * 🔴 Зачем, если есть `fitToScreen`. Вписывание прижимает ленту к левому
 * верхнему углу, а раскрытая группа может стоять в самом низу: человек
 * открывал карту и видел верх ленты без единого узла, о котором ему говорили.
 * Стартовый вид обязан показать корень профессии И выбранную ветвь.
 *
 * Порядок уступок, если всё не помещается:
 * 1. масштаб уменьшается, но не ниже `MIN_ZOOM` — ниже подписи не читаются,
 *    а карточка перестаёт быть зоной нажатия в 44 px;
 * 2. если и так не помещается — центр на ПОСЛЕДНЕЙ карточке списка: вызывающий
 *    кладёт туда самое важное (целевой узел), корень уступает.
 *
 * Масштаб только УМЕНЬШАЕТСЯ относительно переданного — то же свойство, что у
 * вписывания: сцена не раздувается на широком мониторе.
 */
export const revealBoxes = (
  viewport: IViewport,
  boxes: IBox[],
  scene: ISize,
  view: ISize,
  pad = 24,
): IViewport => {
  if (!boxes.length || !view.width || !view.height) return viewport;
  const top = Math.min(...boxes.map((box) => box.y));
  const bottom = Math.max(...boxes.map((box) => box.y + box.h));
  const left = Math.min(...boxes.map((box) => box.x));
  const right = Math.max(...boxes.map((box) => box.x + box.w));
  const target = boxes[boxes.length - 1];

  const needH = bottom - top + 2 * pad;
  const needW = right - left + 2 * pad;
  const zoom = clampZoom(Math.min(viewport.zoom, view.height / needH, view.width / needW));
  const fitsH = needH * zoom <= view.height + 0.5;
  const fitsW = needW * zoom <= view.width + 0.5;
  const cy = fitsH ? (top + bottom) / 2 : target.y + target.h / 2;
  const cx = fitsW ? (left + right) / 2 : target.x + target.w / 2;

  return clampPan(
    { zoom, x: view.width / 2 - cx * zoom, y: view.height / 2 - cy * zoom },
    scene,
    view,
  );
};
